import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await api.get("/blogs");
        setBlogs(res.data);
      } catch (error) {
        console.error("Failed to fetch blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 px-4 md:px-8 py-6 flex justify-between items-center">
        <Link 
          to="/" 
          className="text-xl md:text-2xl font-display font-bold tracking-widest text-white mix-blend-difference"
        >
          XLUMA
        </Link>
        <Link 
          to="/"
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white mix-blend-difference hover:text-accent transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent text-xs uppercase tracking-widest font-bold">Our Blog</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold mt-4 mb-6">
              INSIGHTS &<br />
              <span className="text-transparent text-stroke">STORIES</span>
            </h1>
            <p className="text-gray-400 max-w-xl text-lg">
              Explore our latest thoughts on design, development, and digital innovation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="px-4 md:px-12 pb-32">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, index) => (
                <motion.article
                  key={blog.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={`/blog/${blog.slug}`}>
                    {/* Image */}
                    <div className="aspect-[16/10] overflow-hidden rounded-xl mb-4 bg-neutral-900">
                      {blog.cover_image ? (
                        <img 
                          src={blog.cover_image} 
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-gray-700 font-display text-4xl font-bold">XLUMA</span>
                        </div>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(blog.created_at)}
                      </span>
                      {blog.read_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {blog.read_time} min read
                        </span>
                      )}
                    </div>

                    {/* Category */}
                    {blog.category && (
                      <span className="text-accent text-xs uppercase tracking-widest font-bold">
                        {blog.category}
                      </span>
                    )}

                    {/* Title */}
                    <h2 className="text-xl md:text-2xl font-display font-bold mt-2 mb-3 group-hover:text-accent transition-colors">
                      {blog.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                      {blog.excerpt}
                    </p>

                    {/* Read More */}
                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white group-hover:text-accent transition-colors">
                      Read Article
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
